/**
 * useSimulations - Hook to fetch past outcome simulations for a record/signal
 *
 * Returns simulation history for a given signal ID to display in PastSimulationsTable.
 * Currently uses mock data; will be updated to fetch from backend API when endpoint is available.
 */

import { useState, useEffect } from 'react';
import type { Simulation } from '../components/v4/records/components/PastSimulationsTable';
import { mockSimulations } from '../data/mockSimulations';
import type { OmbudsmanSimulation } from '../types/workspace';

// =============================================================================
// Types
// =============================================================================

interface UseSimulationsOptions {
  enabled?: boolean; // Allow conditional fetching
}

interface UseSimulationsReturn {
  simulations: Simulation[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Map OmbudsmanSimulation to Simulation type for PastSimulationsTable
 */
const mapOmbudsmanSimulation = (ombudsmanSim: OmbudsmanSimulation): Simulation => {
  // Map predicted_outcome to outcome (OmbudsmanOutcome → Simulation outcome)
  // OmbudsmanOutcome: 'upheld' | 'not_upheld' | 'unknown'
  // Simulation outcome: 'upheld' | 'not_upheld' | 'partially_upheld' | 'unknown'
  const outcome: Simulation['outcome'] = ombudsmanSim.predicted_outcome;

  // Convert confidence_score from 0-1 to 0-100
  const confidence = Math.round((ombudsmanSim.confidence_score ?? 0) * 100);

  // Map status (OmbudsmanSimulation status → Simulation status)
  // OmbudsmanSimulation status: 'draft' | 'completed' | 'archived'
  // Simulation status: 'completed' | 'pending' | 'failed' | 'draft'
  const statusMap: Record<OmbudsmanSimulation['status'], Simulation['status']> = {
    completed: 'completed',
    draft: 'draft',
    archived: 'completed', // Treat archived as completed
  };

  return {
    id: ombudsmanSim.id,
    createdAt: ombudsmanSim.created_at,
    outcome,
    confidence,
    signalSummary: ombudsmanSim.title || 'Untitled simulation',
    signalDetail: ombudsmanSim.reasoning || 'No reasoning provided',
    status: statusMap[ombudsmanSim.status] || 'pending',
  };
};

/**
 * Fetch simulations from mock data (filtered by signalId)
 */
const fetchMockSimulations = (signalId: string): Simulation[] => {
  const filteredSims = mockSimulations.filter(
    (sim) => sim.signal?.signal_id === signalId
  );

  return filteredSims.map(mapOmbudsmanSimulation);
};

// =============================================================================
// Hook
// =============================================================================

/**
 * useSimulations - Fetch past outcome simulations for a signal
 *
 * @param signalId - The signal ID to fetch simulations for
 * @param options - Optional configuration
 * @returns Simulations data, loading state, error, and refetch function
 *
 * @example
 * const { simulations, isLoading, error } = useSimulations('sig-12345');
 *
 * @todo Replace mock data with real API call when backend endpoint is available:
 * GET /v1/signals/:id/simulations or GET /v1/ombudsman-decisions/simulations?signal_id=:id
 */
export const useSimulations = (
  signalId: string | null | undefined,
  options: UseSimulationsOptions = {}
): UseSimulationsReturn => {
  const { enabled = true } = options;

  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    if (!signalId || !enabled) {
      setSimulations([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchSimulations = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Simulate network delay
        await new Promise((resolve) => setTimeout(resolve, 300));

        // TODO: Replace with real API call when backend endpoint is ready
        // const response = await fetch(`${config.API_URL}/v1/signals/${signalId}/simulations`);
        // if (!response.ok) throw new Error('Failed to fetch simulations');
        // const data = await response.json();
        // setSimulations(data.simulations.map(mapOmbudsmanSimulation));

        // For now, use mock data
        const mockData = fetchMockSimulations(signalId);
        setSimulations(mockData);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setSimulations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSimulations();
  }, [signalId, enabled, refetchTrigger]);

  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  return {
    simulations,
    isLoading,
    error,
    refetch,
  };
};
